import { apiRequest } from '../lib/api-client';
import type { PageKind, PageStatus, PageView, SeoFields } from './api';

/**
 * Authoring a landing page (context.md sections 72-77). Phase 39.
 *
 * A page is a list of blocks, each a type and a bag of props the server validates against its own
 * schema. Nothing here knows what a `HEADING` needs — the palette says, and the palette comes from
 * the same table the validator uses, so the builder cannot offer a block the server would refuse.
 */

export type BlockType =
  | 'HEADING'
  | 'TEXT'
  | 'IMAGE'
  | 'BUTTON'
  | 'FORM'
  | 'VIDEO'
  | 'SPACER'
  | 'DIVIDER'
  | 'TESTIMONIAL'
  | 'PRICING'
  | 'FAQ'
  | 'COLUMNS';

export type PropKind =
  | 'text'
  | 'richtext'
  | 'url'
  | 'number'
  | 'level'
  | 'align'
  | 'money'
  | 'boolean'
  | 'form'
  | 'list'
  | 'qa';

export interface PropSpec {
  key: string;
  kind: PropKind;
  required: boolean;
}

export interface BlockPaletteEntry {
  type: BlockType;
  nests: boolean;
  props: PropSpec[];
}

export interface Block {
  type: BlockType;
  props: Record<string, unknown>;
  children?: Block[];
}

export interface PageContent {
  blocks: Block[];
  theme?: Record<string, unknown>;
}

export interface PageVersion {
  id: string;
  version: number;
  variant: string | null;
  notes: string | null;
  createdAt: string;
}

export interface PageDetail extends PageView {
  content: PageContent;
  contentVersion: number | null;
  versions: PageVersion[];
  variants: { version: number; variant: string; weight: number }[];
}

export function listBlocks(): Promise<{ blocks: BlockPaletteEntry[] }> {
  return apiRequest('/v1/pages/blocks');
}

export function getPage(id: string): Promise<PageDetail> {
  return apiRequest(`/v1/pages/${id}`);
}

export function createPage(input: {
  slug: string;
  name: string;
  kind?: PageKind;
  content?: PageContent;
}): Promise<PageDetail> {
  return apiRequest('/v1/pages', { method: 'POST', body: input });
}

/**
 * Saves a draft.
 *
 * `expectedVersion` travels with it, as everywhere else a version matters: two people editing the
 * same page must not silently overwrite each other (context.md section 199).
 */
export function savePage(
  id: string,
  input: {
    name?: string;
    kind?: PageKind;
    seo?: SeoFields;
    content?: PageContent;
    notes?: string | null;
    expectedVersion?: number;
  },
): Promise<PageDetail> {
  return apiRequest(`/v1/pages/${id}`, { method: 'PATCH', body: input });
}

export function publishPage(id: string, version?: number): Promise<PageView> {
  return apiRequest(`/v1/pages/${id}/publish`, {
    method: 'POST',
    body: version === undefined ? {} : { version },
  });
}

export function unpublishPage(id: string): Promise<PageView> {
  return apiRequest(`/v1/pages/${id}/unpublish`, { method: 'POST', body: {} });
}

export function deletePage(id: string): Promise<void> {
  return apiRequest(`/v1/pages/${id}`, { method: 'DELETE' });
}

/**
 * The rendered HTML of one version, for the preview pane.
 *
 * Fetched rather than framed by URL: the session is a bearer token held in memory, so an
 * `<iframe src>` would arrive unauthenticated. The document goes into a sandboxed `srcdoc`
 * instead, which is also the stricter of the two — the preview can neither script nor navigate.
 */
export function previewPage(id: string, version?: number): Promise<string> {
  return apiRequest(`/v1/pages/${id}/preview${version ? `?version=${version}` : ''}`, {
    expect: 'text',
  });
}

export type { PageKind, PageStatus, PageView, SeoFields };

// ---------------------------------------------------------------------------
// A/B experiments (context.md section 76)
// ---------------------------------------------------------------------------

export interface VariantSplit {
  /** The version number this variant serves. */
  version: number;
  /** A single capital letter: A, B, C. */
  variant: string;
  /** Percent of traffic. The splits must add up to 100. */
  weight: number;
}

export interface VariantResult {
  variant: string;
  visits: number;
  conversions: number;
  conversionRate: number;
}

export function setExperiment(
  id: string,
  variants: VariantSplit[],
): Promise<{ variants: { variant: string; weight: number }[] }> {
  return apiRequest(`/v1/pages/${id}/experiment`, { method: 'PUT', body: { variants } });
}

export function getExperiment(id: string): Promise<{ variants: VariantResult[] }> {
  return apiRequest(`/v1/pages/${id}/experiment`);
}

// ---------------------------------------------------------------------------
// Starter templates (Phase 44)
// ---------------------------------------------------------------------------

export type TemplateKind = 'PAGE' | 'EMAIL';

export interface TemplateSummary {
  key: string;
  kind: TemplateKind;
  name: string;
  description: string;
}

export function listTemplates(kind?: TemplateKind): Promise<{ templates: TemplateSummary[] }> {
  return apiRequest(`/v1/pages/templates${kind ? `?kind=${kind}` : ''}`);
}

export function getTemplate(key: string): Promise<TemplateSummary & { content: PageContent }> {
  return apiRequest(`/v1/pages/templates/${key}`);
}
